



























































import { Vue, Component, Prop, Watch } from "vue-property-decorator";
// Enums/Data
import { ConfirmModalModes } from "@/data/enums";

// Types
import { DataModules } from "@/store/modules/data";
import {
  IPromotion,
  IPromotionForm,
  IPromotionProduct
} from "@/models/promotions";

@Component({
  components: {
    "selected-promotion-products": () =>
      import(
        "@/components/app/admin/catalogue/promotions/selectedPromotionProducts.vue"
      )
  }
})
export default class SelectedProducts extends Vue {
  public $refs!: {
    selectedSingleProducts: HTMLFormElement;
  };

  // Props
  @Prop({ default: null })
  public promotion!: IPromotion | null;

  @Prop({ required: true })
  public value!: IPromotionForm["products"];

  @Prop({ required: true })
  public isDisabled!: boolean;

  @Prop({ required: true })
  public isProcessing!: boolean;

  @Prop({ required: true })
  public canManagePromotion!: boolean;

  @Prop({ required: true })
  public form!: IPromotionForm;

  @Prop({ default: false })
  public typeIsChanged!: boolean;

  // Data
  public formSelectedProducts: IPromotionProduct[] = this.value?.length
    ? this.value
    : [];

  // Data
  public tableIsProcessing = false;

  public get isUpdate() {
    return !this.$_.isEmpty(this.promotion);
  }

  public get promotionSelectedProducts() {
    return this.$_.get(this.promotion, "products", []);
  }

  // Methods
  public openSelectProductsModal() {
    this.$store.dispatch(
      `data/${DataModules.CATALOGUE}/openSelectProductsModal`,
      {
        props: {
          selectedProducts: this.$_.map(
            this.formSelectedProducts,
            formProduct => {
              return {
                id: formProduct.product_id,
                object_type: "product"
              } as IPromotionProduct;
            }
          )
        },
        events: {
          success: async selectedProducts => {
            this.tableIsProcessing = true;
            try {
              if (!this.isUpdate || this.typeIsChanged) {
                this.fillSelectedProducts(selectedProducts);
                return;
              }

              const newSelectedProducts = this.$_.filter(
                selectedProducts,
                selectedProduct =>
                  !this.$_.some(this.formSelectedProducts, [
                    "product_id",
                    selectedProduct.id
                  ])
              );

              if (newSelectedProducts.length) {
                await Promise.all(
                  this.$_.map(newSelectedProducts, newSelectedProduct => {
                    return this.attachSelectedProduct(
                      this.$_.merge(
                        {
                          product_id: newSelectedProduct.id
                        },
                        this.$_.pick(this.form, [
                          "amount",
                          "billing_cycles",
                          "currencies",
                          "currency_id",
                          "pricelists",
                          "type",
                          "min_quantity",
                          "max_quantity"
                        ])
                      ) as IPromotionProduct
                    );
                  })
                );
              }

              const removedProducts = this.$_.filter(
                this.formSelectedProducts,
                formSelectedProduct =>
                  !this.$_.some(selectedProducts, [
                    "id",
                    formSelectedProduct.product_id
                  ])
              );

              if (removedProducts.length) {
                await Promise.all(
                  this.$_.map(removedProducts, removedProduct => {
                    return this.detachSelectedProduct(
                      this.getPromotionProductId(removedProduct.product_id)
                    );
                  })
                );
              }

              this.$emit("reload-data");

              this.$store.dispatch("toast/show", {
                message: this.$t("_.object_successfully_updated", {
                  object: this.$t("_.promotion")
                })
              });
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            } finally {
              this.$refs.selectedSingleProducts.clearSelectedProducts();
              this.tableIsProcessing = false;
            }
          }
        }
      }
    );
  }

  public async fillSelectedProducts(selectedProducts) {
    const formSelectedProducts = this.$_.map(
      selectedProducts,
      selectedProduct => {
        return { product_id: selectedProduct.id };
      }
    );
    this.$emit("input", formSelectedProducts);
  }

  public attachSelectedProduct(promotionProduct) {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_PROMOTIONS}/attachSelectedProduct`,
      {
        promotionId: this.promotion?.id,
        promotionProduct: this.$store.getters[
          `data/${DataModules.CATALOGUE_PROMOTIONS}/mapToBESchema`
        ](promotionProduct)
      }
    );
  }

  public detachSelectedProduct(productId: IPromotionProduct["id"]) {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_PROMOTIONS}/detachSelectedProduct`,
      {
        promotionId: this.promotion?.id,
        productId
      }
    );
  }

  public detachSelectedProducts(selectedProducts: IPromotionProduct[] = []) {
    this.$store.dispatch("ui/open/confirmModal", {
      config: {
        parent: this,
        props: {
          mode: ConfirmModalModes.BUTTON,
          autoClose: true,
          confirmButtonText: this.$t("_action.detach"),
          message: this.$t("_sentence.detach_confirmation", {
            object: this.getSelectedItemName(selectedProducts.length)
              .toString()
              .toLowerCase()
          })
        },
        canCancel: [],
        events: {
          confirmed: async () => {
            this.tableIsProcessing = true;
            try {
              if (!this.isUpdate || this.typeIsChanged) {
                const formSelectedProducts = this.$_.isEmpty(selectedProducts)
                  ? []
                  : this.$_.filter(
                      this.formSelectedProducts,
                      (product: IPromotionProduct) =>
                        !this.$_.some(selectedProducts, [
                          "product_id",
                          product.product_id
                        ])
                    );
                this.$emit("input", formSelectedProducts);
                return;
              }

              if (this.$_.isEmpty(selectedProducts)) {
                await this.detachAllSelectedProducts();
              } else {
                await Promise.all(
                  this.$_.map(selectedProducts, product => {
                    return this.detachSelectedProduct(
                      this.getPromotionProductId(product.product_id)
                    );
                  })
                );
              }

              this.$emit("reload-data");

              this.$store.dispatch("toast/show", {
                message: this.$t("_.object_successfully_removed", {
                  object: this.getSelectedItemName(selectedProducts.length)
                })
              });
            } catch (error) {
              this.$store.dispatch("api/handleError", error);
            } finally {
              this.$refs.selectedSingleProducts.clearSelectedProducts();
              this.tableIsProcessing = false;
            }
          }
        }
      }
    });
  }

  public detachAllSelectedProducts() {
    return this.$store.dispatch(
      `data/${DataModules.CATALOGUE_PROMOTIONS}/detachAllSelectedProducts`,
      { promotionId: this.promotion?.id }
    );
  }

  public getSelectedItemName(selectedProductsCount) {
    return selectedProductsCount === 1
      ? this.$t("_.product")
      : this.$t("_.products");
  }

  public getPromotionProductId(selectedProductId) {
    return this.$_.get(
      this.$_.find(this.promotion?.products, ["product_id", selectedProductId]),
      "id"
    );
  }

  @Watch("value", { deep: true, immediate: true })
  public onValueChanged(value) {
    this.formSelectedProducts = value;
  }
}
